import gaProvider from '../providers/gaProvider';
import CustomDimensionsDataProvider from '../providers/custom-dimensions-data-provider';
import {
  isBulkAddToShortlistsAction,
  isBulkOfferCustomizedAction,
  isBulkOfferSentAction,
  isCampaignContentAction,
  isCampaignOverviewAction,
  isCampaignReportAction,
  isCampaignsAddressPopupAction,
  isCampaignsDuplicatedAction,
  isCampaignsEditAction,
  isCampaignsHeaderExpandedAction,
  isChangeSortingOfReportingForInstagramPostsUserAction,
  isChangeSortingOfReportingForInstagramReelsUserAction,
  isChangeSortingOfReportingForInstagramStoriesUserAction,
  isChangeSortingOfReportingForTikTokVideosUserAction,
  isChoseActionForBulkOfferUpdateUserAction,
  isCollectionStatsViewedAction,
  isCollectionTabSelectedAction,
  isContextMenuAction,
  isCreatorCollectionDownloadAction,
  isDeliverableAnalyticScreenshotOverrideAction,
  isDeliverableUploadedActionAction,
  isDiscoveryFilterChangeActionAction,
  isDownloadExcelReportV2UserAction,
  isHeaderNotificationsAction,
  isLikeAudienceDataCachedReportAction,
  isLikeAudienceDataNewReportAction,
  isNavigationAction,
  isOfferCardAction,
  isOffersTableAction,
  isProfileClickAction,
  isProfileCoverImageUploadedAction,
  isProfileCreatedAction,
  isProfileImageUploadedAction,
  isProfileSimilarCreatorCriteriaAction,
  isProfileUpdatedAction,
  isReportingSubnavigationAction,
  isRequestDataForNotableLikersUserAction,
  isShowStatDetailsForOfContentCardUserAction,
  isSoVAction,
  isSubnavigationAction,
  isUserDropdownNavigationFinishedUserAction,
  isUserDropdownNavigationStartedUserAction,
  isUserDropdownOpeningUserAction
} from './user-actions';

import {
  UserAction,
  UserActionListener,
  UserDropdownNavigationStartedUserAction
} from '../types/user-actions';
import { isBusinessBankDetailsAction } from './user-actions/business-bank-document-proof';

const getAction = <R extends UserAction>(
  actions: Array<UserAction>,
  guard: (v: UserAction) => v is R
): R | null => (
    actions.reduce<R | null>(
      (acc, val) => {
        if (acc !== null) {
          return acc;
        }

        if (guard(val)) {
          return val;
        }

        return acc;
      },
      null
    )
  );

export const basicActionListener: UserActionListener = actions => {
  const tail = actions[actions.length - 1];

  const typeGuards = [
    isBulkAddToShortlistsAction,
    isBulkOfferSentAction,
    isCampaignReportAction,
    isCampaignOverviewAction,
    isCampaignsDuplicatedAction,
    isDeliverableAnalyticScreenshotOverrideAction,
    isHeaderNotificationsAction,
    isContextMenuAction,
    isOfferCardAction,
    isOffersTableAction,
    isReportingSubnavigationAction,
    isNavigationAction,
    isSubnavigationAction,
    isRequestDataForNotableLikersUserAction,
    isUserDropdownOpeningUserAction
  ];

  if (!typeGuards.some(fn => fn(tail))) {
    return actions;
  }

  const { eventAction, eventCategory } = tail;

  gaProvider.trackGAEvent(
    {
      eventCategory,
      eventAction,
      nonInteraction: false
    },
    CustomDimensionsDataProvider.getGeneralHitScopeData()
  );

  return actions.filter(action => action !== tail);
};

export const userDropdownNavigationFinishedUserActionListener: UserActionListener = actions => {
  const tail = actions[actions.length - 1];

  if (!isUserDropdownNavigationFinishedUserAction(tail)) {
    return actions;
  }

  const finishAction = tail;
  const startAction = getAction(
    actions,
    (action): action is UserDropdownNavigationStartedUserAction =>
      (isUserDropdownNavigationStartedUserAction(action)
        && action.destination === finishAction?.destination)
  );

  if (startAction === null || finishAction === null) {
    return actions;
  }
  const { eventAction, eventCategory } = startAction;
  const eventLabel = startAction.eventLabel.type === 'settings'
    ? `${startAction.eventLabel.type}.${startAction.eventLabel.entity}`
    : `${startAction.eventLabel.type}.${startAction.eventLabel.id}`;

  gaProvider.trackGAEvent(
    {
      eventLabel,
      eventCategory,
      eventAction,
      nonInteraction: false
    },
    {
      ...CustomDimensionsDataProvider.getGeneralHitScopeData(),
      ...startAction.customDimensionData
    }
  );

  return actions.filter(action => (action !== startAction) && (action !== finishAction));
};

export const choseActionForBulkOfferUpdateUserActionListener: UserActionListener = actions => {
  const tail = actions[actions.length - 1];

  if (!isChoseActionForBulkOfferUpdateUserAction(tail)) {
    return actions;
  }

  const { eventAction, eventCategory, eventLabel } = tail;

  gaProvider.trackGAEvent(
    {
      eventCategory,
      eventAction,
      eventLabel,
      nonInteraction: false
    },
    CustomDimensionsDataProvider.getGeneralHitScopeData()
  );

  return actions.filter(action => action !== tail);
};

export const changeSortingOfReportingForInstagramPostsUserActionListener: UserActionListener =
  actions => {
    const tail = actions[actions.length - 1];

    if (!isChangeSortingOfReportingForInstagramPostsUserAction(tail)) {
      return actions;
    }

    const { eventAction, eventCategory, eventLabel } = tail;

    gaProvider.trackGAEvent(
      {
        eventCategory,
        eventAction,
        eventLabel,
        nonInteraction: false
      },
      CustomDimensionsDataProvider.getGeneralHitScopeData()
    );

    return actions.filter(action => action !== tail);
  };

export const changeSortingOfReportingForTikTokVideosActionListener: UserActionListener =
  actions => {
    const tail = actions[actions.length - 1];

    if (!isChangeSortingOfReportingForTikTokVideosUserAction(tail)) {
      return actions;
    }

    const { eventAction, eventCategory, eventLabel } = tail;

    gaProvider.trackGAEvent(
      {
        eventCategory,
        eventAction,
        eventLabel,
        nonInteraction: false
      },
      CustomDimensionsDataProvider.getGeneralHitScopeData()
    );

    return actions.filter(action => action !== tail);
  };

export const changeSortingOfReportingForInstagramStoriesOrReelsActionListener: UserActionListener =
  actions => {
    const tail = actions[actions.length - 1];

    if (
      !isChangeSortingOfReportingForInstagramStoriesUserAction(tail)
      && !isChangeSortingOfReportingForInstagramReelsUserAction(tail)
    ) {
      return actions;
    }

    const { eventAction, eventCategory, eventLabel } = tail;

    gaProvider.trackGAEvent(
      {
        eventCategory,
        eventAction,
        eventLabel,
        nonInteraction: false
      },
      CustomDimensionsDataProvider.getGeneralHitScopeData()
    );

    return actions.filter(action => action !== tail);
  };

export const showStatDetailsForOfContentCardUserActionListener: UserActionListener = actions => {
  const tail = actions[actions.length - 1];

  if (!isShowStatDetailsForOfContentCardUserAction(tail)) {
    return actions;
  }

  const { eventAction, eventCategory, customDimensionData } = tail;

  gaProvider.trackGAEvent(
    {
      eventCategory,
      eventAction,
      nonInteraction: false
    },
    {
      ...CustomDimensionsDataProvider.getGeneralHitScopeData(),
      ...customDimensionData
    }
  );

  return actions.filter(action => action !== tail);
};

export const downloadExcelReportV2UserActionListener: UserActionListener = actions => {
  const tail = actions[actions.length - 1];

  if (!isDownloadExcelReportV2UserAction(tail)) {
    return actions;
  }

  const { eventAction, eventCategory, eventLabel } = tail;

  gaProvider.trackGAEvent(
    {
      eventCategory,
      eventAction,
      eventLabel,
      nonInteraction: false
    },
    CustomDimensionsDataProvider.getGeneralHitScopeData()
  );

  return actions.filter(action => action !== tail);
};

export const collectionTabSelectedActionListener: UserActionListener = actions => {
  const tail = actions[actions.length - 1];

  if (!isCollectionTabSelectedAction(tail)) {
    return actions;
  }

  const { eventAction, eventCategory, eventLabel } = tail;

  gaProvider.trackGAEvent(
    {
      eventCategory,
      eventAction,
      eventLabel,
      nonInteraction: false
    },
    CustomDimensionsDataProvider.getGeneralHitScopeData()
  );

  return actions.filter(action => action !== tail);
};

export const creatorCollectionDownloadActionListener: UserActionListener = actions => {
  const tail = actions[actions.length - 1];

  if (!isCreatorCollectionDownloadAction(tail)) {
    return actions;
  }

  const { eventAction, eventCategory, customDimensionData } = tail;

  gaProvider.trackGAEvent(
    {
      eventCategory,
      eventAction,
      nonInteraction: false
    },
    {
      ...CustomDimensionsDataProvider.getGeneralHitScopeData(),
      ...customDimensionData
    }
  );

  return actions.filter(action => action !== tail);
};

export const collectionStatsViewedActionListener: UserActionListener = actions => {
  const tail = actions[actions.length - 1];

  if (!isCollectionStatsViewedAction(tail)) {
    return actions;
  }

  const { eventAction, eventCategory, eventLabel, customDimensionData } = tail;

  gaProvider.trackGAEvent(
    {
      eventCategory,
      eventAction,
      eventLabel,
      nonInteraction: false
    },
    {
      ...CustomDimensionsDataProvider.getGeneralHitScopeData(),
      ...customDimensionData
    }
  );

  return actions.filter(action => action !== tail);
};

export const bulkOfferCustomizedActionListener: UserActionListener = actions => {
  const tail = actions[actions.length - 1];

  if (!isBulkOfferCustomizedAction(tail)) {
    return actions;
  }

  const { eventAction, eventCategory, eventLabel } = tail;

  gaProvider.trackGAEvent(
    {
      eventCategory,
      eventAction,
      eventLabel,
      nonInteraction: false
    },
    CustomDimensionsDataProvider.getGeneralHitScopeData()
  );

  return actions.filter(action => action !== tail);
};

export const likeAudienceDataActionListener: UserActionListener = actions => {
  const tail = actions[actions.length - 1];

  if (
    !isLikeAudienceDataCachedReportAction(tail)
    && !isLikeAudienceDataNewReportAction(tail)
  ) {
    return actions;
  }

  const { eventAction, eventCategory, eventLabel } = tail;

  gaProvider.trackGAEvent(
    {
      eventCategory,
      eventAction,
      eventLabel,
      nonInteraction: false
    },
    CustomDimensionsDataProvider.getGeneralHitScopeData()
  );

  return actions.filter(action => action !== tail);
};


export const campaignsActionWithLabelListener: UserActionListener = actions => {
  const tail = actions[actions.length - 1];

  if (
    !isCampaignsHeaderExpandedAction(tail)
    && !isCampaignsEditAction(tail)
    && !isCampaignsAddressPopupAction(tail)
  ) {
    return actions;
  }

  const { eventAction, eventCategory, eventLabel } = tail;

  gaProvider.trackGAEvent(
    {
      eventCategory,
      eventAction,
      eventLabel,
      nonInteraction: false
    },
    CustomDimensionsDataProvider.getGeneralHitScopeData()
  );

  return actions.filter(action => action !== tail);
};

export const profileActionWithLabelListener: UserActionListener = actions => {
  const tail = actions[actions.length - 1];

  if (
    !isProfileClickAction(tail)
    && !isProfileCoverImageUploadedAction(tail)
    && !isProfileCreatedAction(tail)
    && !isProfileImageUploadedAction(tail)
    && !isProfileSimilarCreatorCriteriaAction(tail)
    && !isProfileUpdatedAction(tail)
  ) {
    return actions;
  }

  const { eventAction, eventCategory, eventLabel } = tail;

  gaProvider.trackGAEvent(
    {
      eventCategory,
      eventAction,
      eventLabel,
      nonInteraction: false
    },
    CustomDimensionsDataProvider.getGeneralHitScopeData()
  );

  return actions.filter(action => action !== tail);
};

export const discoveryFiltersActionListener: UserActionListener = actions => {
  const tail = actions[actions.length - 1];

  if (!isDiscoveryFilterChangeActionAction(tail)) {
    return actions;
  }

  const { eventAction, eventCategory } = tail;
  const eventLabel = ((): string => {
    if (tail.eventLabel instanceof Array) {
      return tail.eventLabel.join(' & ');
    }

    if (typeof tail.eventLabel === 'string') {
      return tail.eventLabel;
    }

    const { matchMode } = tail.eventLabel;
    const excluded = tail.eventLabel.excluded.join(` ${matchMode} `);
    const included = tail.eventLabel.included.join(` ${matchMode} `);
    return `${included} | ${excluded}`;
  })();

  gaProvider.trackGAEvent(
    {
      eventCategory,
      eventAction,
      eventLabel,
      nonInteraction: false
    },
    CustomDimensionsDataProvider.getGeneralHitScopeData()
  );

  return actions.filter(action => action !== tail);
};

export const campaignContentActionListener: UserActionListener = actions => {
  const tail = actions[actions.length - 1];

  if (!isCampaignContentAction(tail)) {
    return actions;
  }

  const { eventAction, eventCategory, eventLabel } = tail;

  gaProvider.trackGAEvent(
    {
      eventCategory,
      eventAction,
      eventLabel,
      nonInteraction: false
    },
    CustomDimensionsDataProvider.getGeneralHitScopeData()
  );

  return actions.filter(action => action !== tail);
};

export const deliverableUploadedActionActionListener: UserActionListener = actions => {
  const tail = actions[actions.length - 1];

  if (!isDeliverableUploadedActionAction(tail)) {
    return actions;
  }

  const { eventAction, eventCategory, eventLabel } = tail;

  gaProvider.trackGAEvent(
    {
      eventCategory,
      eventAction,
      eventLabel,
      nonInteraction: false
    },
    CustomDimensionsDataProvider.getGeneralHitScopeData()
  );

  return actions.filter(action => action !== tail);
};

export const sovActionListener: UserActionListener = actions => {
  const tail = actions[actions.length - 1];

  if (!isSoVAction(tail)) {
    return actions;
  }

  const { eventAction, eventCategory, eventLabel } = tail;

  gaProvider.trackGAEvent(
    {
      eventCategory,
      eventAction,
      eventLabel,
      nonInteraction: false
    },
    CustomDimensionsDataProvider.getGeneralHitScopeData()
  );

  return actions.filter(action => action !== tail);
};

export const businessBankDetailsActionListener: UserActionListener = actions => {
  const tail = actions[actions.length - 1];

  if (!isBusinessBankDetailsAction(tail)) {
    return actions;
  }

  const { eventAction, eventCategory, eventLabel } = tail;

  gaProvider.trackGAEvent(
    {
      eventCategory,
      eventAction,
      eventLabel
    },
    CustomDimensionsDataProvider.getGeneralHitScopeData()
  );

  return actions.filter(action => action !== tail);
};
